const BlockPrefabController = require('BlockPrefabController');
const AudioManager = require('AudioManager');
const AudioMap = require('AudioMap');
const timeScale = 1;

const State = cc.Enum({
    START: 'start',
    SPIN: 'spin',
    IDLE: 'idle',
    STOP: 'stop'
});

cc.Class({
    extends: cc.Component,

    properties: {
        blockPrefab: cc.Prefab,
        symbolContainer: cc.Node,
        blockPrefab: cc.Prefab,
        anticipation: sp.Skeleton
    },

    ctor () {
        this.activeSymbols = [];
        this.allSymbols = [];
        this.reelData = [];
        this.stickySymbols = new Map();
        this.state = State.IDLE;
    },

    onLoad () {
        this.audioManager = cc.find('AudioManager').getComponent(AudioManager);
        this.audioMap = cc.find('AudioMap').getComponent(AudioMap);
        this.blockPool = new cc.NodePool(BlockPrefabController);
        this.anticipation.node.active = false;
        this.anticipation.setCompleteListener(() => {
            this.anticipation.node.active = false;
        });
        let block;

        for (let i = 0; i < 12; i++) {
            block = cc.instantiate(this.blockPrefab);
            this.blockPool.put(block);
        }

        this.blockHeight = this.blockPrefab.data.height;
    },

    scatterCount () {
        return this.reelData.reduce((s, item) => {
            if (item === 'SC') {
                return s + 1;
            }
            return s;
        }, 0)
    },

    setPrecookedReel (data) {
        this.precookedReelData = data;
        this.precookedReelIndex = 0;
    },

    setWildPlacements (placedSpecialWilds) {
        this.placedSpecialWilds = [false, false, false];
        placedSpecialWilds.forEach(item => this.placedSpecialWilds[item.row] = true);
    },

    setData (reelData) {
        this.placedSpecialWilds = null;
        this.reelData = reelData;
        this.node.width = this.blockPrefab.data.width;
        this.node.height = reelData.length * this.blockPrefab.data.height;

        if (this.stickySymbols.size > 0) {
            for (const [key] of this.stickySymbols) {
                this.reelData[key] = this._getNextPrecookedType();
            }
        }
    },

    getBlockController (index) {
        if (index < 0 || index >= this.reelData.length) {
            cc.warn('getBlock index is out of range');
        }
        return this.activeSymbols[index];
    },

    setInitData (reelData, index) {
        this.index = index;
        this.setData(reelData);
        reelData.forEach((blockType, index) => {
            const block = this._createBlock(index, blockType, true);

            this.activeSymbols.push(block);
            this.allSymbols.push(block);
        });
        let precookSymbolsCounter = 0;

        // create precooked reels in top of real symbols for visual reel spin action
        while (precookSymbolsCounter < reelData.length) {
            const block = this._createBlock(-(++precookSymbolsCounter),
                this._getNextPrecookedType());

            block.node.active = false;
            this.allSymbols.unshift(block);
        }
        this.allSymbolsLength = this.allSymbols.length;
    },

    spinReel () {
        return new Promise((resolve) => {
            if (this.state === State.SPIN) {
                resolve();
            }
            this._changeState(State.START);
            this.node.once(State.SPIN, () => {
                resolve();
            });
        });
    },

    stopReel (hasAnticipation, winAnticipation, index, hasSpecialWildData) {
        return new Promise((resolve) => {
            if (this.state === State.IDLE) {
                cc.error('Try to stop idle reel!');
                resolve();
            }
            this.hasSpecialWildData = hasSpecialWildData;
            this.hasAnticipation = hasAnticipation || winAnticipation;
            if (this.hasAnticipation) {
                const time = hasAnticipation ? 2 : 1;
                this.node.once('spinStep', () => {
                    if (hasAnticipation) {
                        this.anticipation.node.active = true;
                        this.anticipation.setAnimation(0, '1', false);
                        this.audioMap.playSfx('reelAnticipation' + index);
                    }
                    this.symbolContainer.stopAllActions();
                    // hack to call the code for resetting the SPIN state (if the state is the same, it ignores)
                    this._changeState('');
                    this._changeState(State.SPIN);
                });
                this.scheduleOnce(() => {
                    this._changeState(State.STOP);
                }, time * timeScale);
            } else {
                this._changeState(State.STOP);
            }

            this.node.once(State.IDLE, () => {
                resolve();
            });
        });
    },

    overrideSymbol (row, newSymType) {
        this.reelData[row] = newSymType;
        // This function is called only in FS state when we override first symbol
        // as wild to show the expansion, so the wildIndex is the row
        return this.getBlockController(row).overrideSymbol(newSymType);
    },

    getData () {
        return this.reelData;
    },

    isIdle () {
        return this.state === State.IDLE;
    },

    isSpinning () {
        return this.state === State.SPIN;
    },

    isStopped () {
        return this.state === State.STOP;
    },

    hasScatter () {
        return this.reelData.includes('SC');
    },

    getWorldCoord () {
        return this.node.convertToWorldSpaceAR(this.node.position);
    },

    _getNextPrecookedType () {
        return this.precookedReelData[this.precookedReelIndex++ % this.precookedReelData.length];
    },

    _spinHandler () {
        const bottomSymbol = this.allSymbols.pop();

        const type = this._getNextPrecookedType();

        bottomSymbol.node.y += this.allSymbolsLength * this.blockHeight;
        bottomSymbol.setType(type);
        bottomSymbol.setBlur();
        this.allSymbols.unshift(bottomSymbol);
        this.node.emit('spinStep');
    },

    stopAnticipationGlow () {
        this.activeSymbols.forEach((block) => {
            block.stopAnticipationGlow();
        })
    },

    _changeState (state) {
        if (state === this.state) {
            return;
        }
        this.oldState = this.state;
        this.state = state;
        switch (state) {
            case State.IDLE:
                const { y } = this.symbolContainer;

                this.hasAnticipation = false;
                this.symbolContainer.y = 0;
                this.symbolContainer.children.forEach((node) => {
                    node.y += y;
                });
                if (this.stickySymbols.size > 0) {
                    for (const [key, val] of this.stickySymbols) {
                        if (this.stickySymbols.has(key)) {
                            this.activeSymbols[key] = val.stickySymbol;
                        }
                    }
                }
                this.node.emit(this.state);
                break;
            case State.START:
                this._onStartSpin();
                break;
            case State.SPIN:
                this.allSymbols.forEach((block) => {
                    block.setBlur();
                });
                this.symbolContainer.runAction(cc.repeatForever(
                    cc.sequence(
                        cc.moveBy(this.hasAnticipation ? 0.09 * timeScale : 0.03 * timeScale, 0, -this.blockHeight),
                        cc.callFunc(this._spinHandler, this)
                    )
                )
                );
                this.node.emit(this.state);
                break;
            case State.STOP:
                this.node.once('spinStep', this._stopHandler, this);
                break;
        }
    },

    highlightScatters () {
        this.reelData.forEach((blockType, index) => {
            const block = this.getBlockController(index);
            if (blockType === 'SC') {
                block.playAnticipationGlow();
            }
        });
    },

    _stopHandler () {
        this.symbolContainer.cleanup();
        this.blockPool.put(this.allSymbols.shift().node);
        this.activeSymbols.length = 0;

        this.reelData.forEach((blockType, index) => {
            const block = this._createBlock(-this.reelData.length + index, blockType);
            this.activeSymbols.push(block);
            this.allSymbols.splice(index, 0, block);
            //  hide wild symbol behind of special wild
            if (this.hasSpecialWildData && blockType.includes('WI_SP')) {
                block.hideSpecialWild();
            }

            if (this.hasAnticipation && blockType === 'SC') {
                block.playAnticipationGlow();
            }
            if (this.placedSpecialWilds && this.placedSpecialWilds[index]) {
                // block.playWildImpact();
                // this.node.emit('wildPlaced');
            } else if (blockType.includes('WI_SP')) {
                block.playWildImpact();
            }
            if (blockType.includes('SC')) {
                block.impact();
            }
        });

        // add additional symbol to show a bit for easeBackOut
        const block = this._createBlock(-this.reelData.length - 1, this._getNextPrecookedType());

        this.allSymbols.unshift(block);

        this.blockPool.put(this.allSymbols.shift().node);
        this.symbolContainer.runAction(cc.sequence(
            cc.moveBy(this.hasAnticipation ? 1.8 * timeScale : 0.3 * timeScale, 0, -this.reelData.length * this.blockHeight).
                easing(cc.easeBackOut()),
            cc.callFunc(() => {
                this.allSymbolsLength = this.allSymbols.length;
                for (let i = this.reelData.length; i < this.allSymbolsLength; i++) {
                    const block = this.allSymbols.pop();

                    this.allSymbols.unshift(block);
                    block.node.active = false;
                    block.node.y += this.allSymbolsLength * this.blockHeight;
                }
                // eslint-disable-next-line no-unused-vars
                let dif = this.allSymbols[0].node.y;

                this.allSymbols.forEach((symbol) => {
                    dif -= symbol.node.y;
                    dif = symbol.node.y;
                });

                this._changeState(State.IDLE);
            })
        )
        );
    },

    _onStartSpin () {
        this.node.zIndex = this.node.index;
        this.symbolContainer.runAction(cc.sequence(
            cc.moveBy(0.2 * timeScale, 0,  this.blockHeight / 3).
                easing(cc.easeCubicActionOut()),
            cc.callFunc(this._emitTop, this),
            cc.moveBy(0.2 * timeScale, 0, -this.reelData.length * this.blockHeight - this.blockHeight / 3).
                easing(cc.easeCubicActionIn()),
            cc.callFunc(() => {
                for (let i = this.allSymbolsLength / 2; i < this.allSymbolsLength - 1; i++) {
                    this.blockPool.put(this.allSymbols.pop().node);
                }
                this.allSymbolsLength = this.allSymbols.length;

                const block = this.allSymbols.pop();

                block.node.y += this.allSymbolsLength * this.blockHeight;

                const type = this._getNextPrecookedType();
                block.setType(type);
                this.allSymbols.unshift(block);
                this._changeState(State.SPIN);
            })
            )
        );
    },

    _emitTop () {
        for (let i = 0; i < this.allSymbolsLength; i++) {
            this.allSymbols[i].node.active = true;
        }
    },
    /**
     * Creates ( retrieves from objects' pool ) block and sets it's position
     * @param {Number} index - need for setting the position (0 index bottom, 1 - middle, 2 - top)
     * @param {Number} blockType
     */
    _createBlock (index, blockType, isInitialData) {
        const block = this.blockPool.get();

        block.zIndex = index;
        this.symbolContainer.addChild(block);
        const y = -index  * this.blockHeight - this.symbolContainer.y - this.blockHeight / 2;

        const blockContoller = block.getComponent(BlockPrefabController);

        blockContoller.node.x = 0;
        blockContoller.node.y = y;
        blockContoller.symbolContainer = this.symbolContainer;
        blockContoller.setType(blockType, isInitialData);
        blockContoller.idle();

        return blockContoller;
    },
});
